import {
  call, put, cancel, select,
} from 'redux-saga/effects';
import amplitude from 'amplitude-js';
import { getAnalyticsConfig } from '@ziynx/analytics';
import ActionTypes from '../actionTypes';

type PostTrackingAction = {
  type: string,
  id: string,
  body: { ids: Array<string> },
};

type AmplitudeInstance = {
  _isInitialized: boolean, // Amplitude client
};

export function* changeUser(action: {
  sub: string,
}): Generator<any, void, empty> {
  const { sub } = action;
  if (!sub) throw new Error('No Sub Passsed!');

  const { analyticsEnabled } = getAnalyticsConfig();

  try {
    if (!analyticsEnabled) {
      yield cancel();
    }

    /** AMPLITUDE */
    yield call(() => {
      const ampInstance: AmplitudeInstance = amplitude.getInstance();

      if (!ampInstance._isInitialized) return;

      ampInstance.setUserId(sub);
    });

    yield put({
      type: ActionTypes.analytics.changeUser.SUCCESS,
    });
  } catch (err) {
    yield put({
      type: ActionTypes.analytics.changeUser.ERROR,
      response: { isLoggedIn: false },
    });
  }
}

export function* logEvent(action: {
  name: string,
  properties: Object,
}): Generator<any, void, empty> {
  const { analyticsEnabled } = getAnalyticsConfig();

  const state = yield select(({ user }) => user?.GET_USER_INIT_API?.company);

  const companyInfo = {
    companyName: state?.name,
    companyId: state?._id,
  };

  try {
    if (!analyticsEnabled) {
      yield cancel();
    }

    const { name, properties = {} } = action;

    if (!name) {
      throw new Error('You must include the name of the event you wish to log');
    }

    /** AMPLITUDE log event
     * @see https://developers.amplitude.com/docs/javascript#events-with-properties
    */
    yield call(() => {
      const ampInstance: AmplitudeInstance = amplitude.getInstance();

      if (!ampInstance._isInitialized) return;

      ampInstance.logEvent(name, { ...properties, ...companyInfo });
    });

    yield put({
      type: ActionTypes.analytics.logEvent.SUCCESS,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.analytics.logEvent.ERROR,
      error,
    });
  }
}

export function* setCustomAttribute(action: {
  key: string,
  value: any,
}): Generator<any, void, empty> {
  const { analyticsEnabled } = getAnalyticsConfig();

  try {
    if (!analyticsEnabled) {
      yield cancel();
    }

    const {
      key,
      // value = {}
    } = action;
    if (!key) {
      throw new Error('You must include the name of the attribute you wish to log');
    }

    /** @see https://developers.amplitude.com/docs/javascript#setting-a-user-property */
    yield call(() => {
      const ampInstance: AmplitudeInstance = amplitude.getInstance();

      if (!ampInstance._isInitialized) return;

      const identify = new amplitude.Identify();
      ampInstance.identify(identify);
    });

    yield put({
      type: ActionTypes.analytics.setCustomAttribute.SUCCESS,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.analytics.setCustomAttribute.ERROR,
      error,
    });
  }
}

/** BRAZE only
 * @see https://www.braze.com/docs/developer_guide/platform_integration_guides/web/analytics/tracking_sessions/
*/
export function* openSession(): Generator<any, void, empty> {
  const { analyticsEnabled } = getAnalyticsConfig();

  try {
    if (!analyticsEnabled) {
      yield cancel();
    }

    yield put({
      type: ActionTypes.analytics.openSession.SUCCESS,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.analytics.openSession.ERROR,
      error,
    });
  }
}

export function* postTrackingPlay(action: PostTrackingAction): Generator<any, void, empty> {
  try {
    const response = yield call(() => APIService.apiRequest('POST_TRACKING_PLAY', 'POST', {
      routeParams: {
        id: action?.id,
      },
      body: { ...action?.body, type: 'play' },
    }));

    yield put({
      type: ActionTypes.analytics.trackPlay.SUCCESS,
      response,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.analytics.trackPlay.ERROR,
    });
  }
}

export function* postTrackingView(action: PostTrackingAction): Generator<any, void, empty> {
  try {
    const response = yield call(() => APIService.apiRequest('POST_TRACKING_VIEW', 'POST', {
      routeParams: {
        id: action?.id,
      },
      body: { ...action?.body, type: 'view' },
    }));

    yield put({
      type: ActionTypes.analytics.trackView.SUCCESS,
      response,
    });
  } catch (error) {
    yield put({
      type: ActionTypes.analytics.trackView.ERROR,
    });
  }
}
